import { useEffect, useState, type ReactNode } from 'react';
import type { PlayerRow, WindowsInfo } from '@/api/types';
import { dash, formatCount, formatDateTime, formatDay, formatMoney, formatName } from '@/lib/format';
import { toPlayerId } from '@/lib/player-id';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { Notice } from '@/ui/Notice';
import { Table, Td, Th } from '@/ui/Table';
import { WINDOWS, windowHeading } from './windows';

type CopyState = 'idle' | 'copied' | 'failed';

const COPY_LABEL: Record<CopyState, string> = {
  idle: 'Copy ID',
  copied: 'Copied',
  failed: 'Copy failed',
};

// No invitation time means one of two things: the player joined through an open link, or the
// issue of their link was never recorded (a tracking outage). Only the first means nobody invited them.
function invitedLabel(player: PlayerRow): string {
  if (player.invitedAt) return formatDateTime(player.invitedAt);
  return player.joinedViaOpenLink ? 'Not invited' : 'Not recorded';
}

function CopyPlayerId({ playerId }: { playerId: string }) {
  const [state, setState] = useState<CopyState>('idle');

  useEffect(() => {
    if (state === 'idle') return;
    const timer = setTimeout(() => setState('idle'), 2000);
    return () => clearTimeout(timer);
  }, [state]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(playerId);
      setState('copied');
    } catch {
      setState('failed');
    }
  }

  return (
    <Button variant="secondary" onClick={() => void copy()}>
      {COPY_LABEL[state]}
    </Button>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-3 text-sm font-semibold text-slate-900">{title}</h3>
      {children}
    </section>
  );
}

function Detail({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-0.5 break-words text-sm">{children}</dd>
    </div>
  );
}

const DETAIL_GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3';

export function PlayerDetailTitle({ playerUuid, isTest }: { playerUuid: string; isTest?: boolean }) {
  return (
    <span className="flex flex-wrap items-center gap-3">
      <span className="font-mono text-sm">{toPlayerId(playerUuid)}</span>
      {isTest && <Badge value="test account" />}
      <CopyPlayerId playerId={toPlayerId(playerUuid)} />
    </span>
  );
}

export function PlayerDetail({ player, windows }: { player: PlayerRow; windows: WindowsInfo }) {
  const { profile } = player;
  const metrics = player.windows;

  return (
    <div className="space-y-6">
      {player.isTest && (
        <Notice>
          Test account — test accounts are left out of the player data, which is why there is no profile here. The link
          and activity figures below are still this account's own.
        </Notice>
      )}
      {!player.isTest && !profile && (
        <Notice>No profile came back for this player; only the link and activity figures are known.</Notice>
      )}

      <Section title="Player">
        <dl className={DETAIL_GRID}>
          <Detail label="Username">{profile?.username ?? dash}</Detail>
          <Detail label="Name">{formatName(profile)}</Detail>
          <Detail label="Email">{profile?.email ?? dash}</Detail>
          <Detail label="Country">{profile?.country ?? dash}</Detail>
          <Detail label="Currency">{profile?.currency ?? dash}</Detail>
          <Detail label="Registered">{formatDateTime(profile?.registrationDate)}</Detail>
          <Detail label="Account status">
            <Badge value={profile?.status ?? null} />
          </Detail>
        </dl>
      </Section>

      <Section title="Link">
        <dl className={DETAIL_GRID}>
          <Detail label="Invited by">{player.agentIds.join(', ') || dash}</Detail>
          <Detail label="Links issued">{formatCount(player.linksIssued)}</Detail>
          <Detail label="Language">{player.locale ?? dash}</Detail>
          <Detail label="Invited">{invitedLabel(player)}</Detail>
          <Detail label="Joined">
            {player.redeemedAt ? formatDateTime(player.redeemedAt) : 'Not joined'}
            {player.joinedViaOpenLink && ' through an open link'}
          </Detail>
          <Detail label="Last active">{formatDay(player.lastActiveDay)}</Detail>
        </dl>
      </Section>

      <Section title="Activity">
        {metrics ? (
          <Table>
            <thead>
              <tr>
                <Th align="left">Window</Th>
                <Th align="center">Active</Th>
                <Th>Deposits</Th>
                <Th>Deposits €</Th>
                <Th>GGR €</Th>
                <Th>NGR €</Th>
              </tr>
            </thead>
            <tbody>
              {WINDOWS.map((window) => {
                const row = metrics[window.key];
                return (
                  <tr key={window.key}>
                    <Td align="left" className={windows[window.key].partial ? 'text-amber-700' : ''}>
                      {windowHeading(windows, window.key, window.label)}
                    </Td>
                    <Td align="center">{row.active ? 'yes' : 'no'}</Td>
                    <Td>{formatCount(row.depositCount)}</Td>
                    <Td>{formatMoney(row.depositEur)}</Td>
                    <Td>{formatMoney(row.ggrEur)}</Td>
                    <Td>{formatMoney(row.ngrEur)}</Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        ) : (
          <p className="text-sm text-slate-500">This player has not joined, so there are no activity figures.</p>
        )}
      </Section>
    </div>
  );
}
